/**
 * EventItem Component for Strategic Module
 * 
 * This component follows the same logic as the Opinion module's EventItem:
 * @see src/pages/opinion/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/Charts/BarAndLine/EventWindows/EventItem.tsx
 * 
 * Key differences from Opinion:
 * - Uses 'dbs-' prefix instead of 'dbo-' for CSS class names (Strategic module naming convention)
 * - Displays game cover and game title (Opinion only shows event text)
 * - Different card styling
 * - Uses different API field names: cover, game_title, event_content, date, studio_name
 * - Many feature is diabled, for example: Mark area time and Bubble 
 * - instead of using Mark area time, more complex handleBoxMouseEnter logic is used to make the text at x-axis bold 
 * - Syncs hover highlight with chart runtime xAxis data (not static props), so custom
 *   axis transforms (e.g. year separator insertion) are preserved while hovering.
 */
import { useEffect, useRef, useState } from 'react';
import './EventItem.scss';
import moment from 'moment';
import React from 'react';
// import { getLocale } from 'umi';
// import _ from 'lodash-es';
import { comparableTime } from '@op/utils/time';
import { ParamsTimeType } from '@op/types/common';
import { getRealXAxisValue } from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/utils';
import CommonBubble from '@home/components/CommonBubble';
import GameDefault from '@/pages/home/assets/img/common/game-default.svg';
// import { LANGUAGE_EN } from '@home/_config/config';
import EllipsisText from '../EllipsisText';

const seriesName = 'TopTipsEvent';

export default function EventItem({
  myChart,
  xAxisValue,
  eventList,
  hide = true,
  format,
  backPositionInfo,
  halfBarWidth,
  maxTimeValue,
  showEventAxis,
  dateType,
  xAxisData,
  yAxisMin = false,
  eventBoxClassName = '',
}: {
  myChart: any;
  xAxisValue: string;
  eventList: Array<any>;
  hide?: boolean;
  format: string;
  halfBarWidth: number;
  maxTimeValue: string;
  showEventAxis: boolean;
  dateType: ParamsTimeType;
  xAxisData: Array<string>;
  yAxisMin?: boolean;
  eventBoxClassName?: string;
  backPositionInfo: (item: {
    eventLine: any;
    barLeftPosition: number;
    barBottomPosition: number;
    eventBox: any;
  }) => void;
}) {
  const eventBoxRef = useRef<any>(null);
  const eventLineRef = useRef<any>(null);
  const currentItemPosition = useRef<number>(0);

  function getBarLeftPosition({
    seriesName,
    xAxis = xAxisValue,
  }: {
    seriesName: string;
    xAxis?: string;
  }) {
    let realXAxisValue = getRealXAxisValue({
      xAxis,
      dateType,
      format,
      xAxisData,
    });

    try {
      // 获取 Y 轴的配置
      const option = myChart.getOption();
      const yAxis = option?.yAxis?.[0];

      // 如果设置了 yAxisMin，使用图表底部对应的 Y 值
      // 否则使用 0
      let yValue = 0;
      if (yAxisMin && yAxis) {
        // 获取 Y 轴的实际范围
        const yAxisModel = myChart.getModel().getComponent('yAxis', 0);
        if (yAxisModel) {
          const scale = yAxisModel.axis.scale;
          const extent = scale.getExtent();
          // 使用 Y 轴的最小值
          yValue = extent[0];
        }
      }

      // 获取柱子底部的像素坐标
      const bottomPixelPosition = myChart.convertToPixel(
        { seriesName: seriesName },
        [realXAxisValue, yValue],
      );

      return { left: bottomPixelPosition[0], bottom: bottomPixelPosition[1] };
    } catch (error) {
      return { left: 0, bottom: 0 };
    }
  }

  function setEventBoxPosition({ seriesName }: { seriesName: string }) {
    // 获取柱子底部的像素坐标
    const leftPosition = getBarLeftPosition({ seriesName });
    currentItemPosition.current = leftPosition.left;
    if (eventBoxRef.current) {
      backPositionInfo({
        barLeftPosition: leftPosition.left,
        barBottomPosition: leftPosition.bottom,
        eventBox: eventBoxRef.current,
        eventLine: eventLineRef.current,
      });
    }
  }

  useEffect(() => {
    if (myChart) {
      // Use requestAnimationFrame to ensure Safari has completed layout
      // before measuring positions with convertToPixel
      requestAnimationFrame(() => {
        setEventBoxPosition({ seriesName });
      });
    }
  }, [myChart]);

  // Check for intersection periodically when EventWindow is visible
  useEffect(() => {
    if (!hide && myChart && eventBoxRef.current) {
      console.log(`[Intersection] Scheduling check for xAxisValue: ${xAxisValue}`);
      
      // Wait longer to ensure:
      // 1. EventWindows have calculated and reported maxBubbleHeight
      // 2. Parent has updated chart's grid.top
      // 3. ECharts has re-rendered with new grid position
      const timeoutId = setTimeout(() => {
        console.log(`[Intersection] Running delayed check for xAxisValue: ${xAxisValue}`);
        const hasIntersect = checkIntersection();
        setHasIntersection(hasIntersect);
      }, 500); // Increased from 200ms to 500ms to wait for chart grid update

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [hide, myChart, eventList]);

  const [renderMarkArea, setRenderMarkArea] = useState({
    width: '0px',
    left: '0px',
  });
  const [eventLineShow, setEventLineShow] = useState(false);
  const [markAreaShow, setMarkAreaShow] = useState(false);
  const [markAreaTimes, setMarkAreaTimes] = useState<string>('');
  // DISABLED: Intersection detection logic (keep for future use if needed)
  const [hasIntersection, setHasIntersection] = useState(false);
  // Read xAxis data from the chart's current option first. This keeps EventItem in sync
  // with runtime xAxis mutations (e.g. separator marker insertion in business charts).
  const getCurrentXAxisData = (): string[] => {
    const currentXAxisData = myChart?.getOption?.()?.xAxis?.[0]?.data;
    if (Array.isArray(currentXAxisData) && currentXAxisData.length > 0) {
      return currentXAxisData.map((item: any) => {
        if (item && typeof item === 'object' && item.value !== undefined) {
          return item.value;
        }
        return item;
      });
    }
    return xAxisData || [];
  };

  const handleMouseEnter = (item: any) => {
    // API uses 'date' field instead of start_time/end_time
    // For single-day events, date is both start and end
    if (!item.date) {
      return;
    }
    const eventDate = moment(item.date).format(format);
    // 大于最大轴
    const maxThanMaxTime = comparableTime(maxTimeValue, eventDate);
    const realXAxis = maxThanMaxTime ? maxTimeValue : eventDate;

    const left = currentItemPosition.current - halfBarWidth;

    const endItem = getBarLeftPosition({ seriesName, xAxis: realXAxis });
    const endLeftValue = endItem.left + halfBarWidth;
    const width = endLeftValue - left;

    setMarkAreaTimes(eventDate);
    setRenderMarkArea({ left: left + 'px', width: width + 'px' });
    setMarkAreaShow(true);
  };

  const handleMouseLeave = () => {
    setMarkAreaShow(false);
    setEventLineShow(false);
    setMarkAreaTimes('');
    setRenderMarkArea({ left: '0', width: '0' });
    if (myChart) {
      // Restore xAxis using current chart data to avoid removing runtime-added axis markers.
      const currentXAxisData = getCurrentXAxisData();
      myChart.setOption({
        xAxis: {
          data: currentXAxisData,
        }
      });
    }
  };

  /**
   * Check if EventWindow intersects with any chart series lines or data labels
   */
  const checkIntersection = () => {
    if (!myChart || !eventBoxRef.current) {
      return false;
    }

    try {
      const eventBoxRect = eventBoxRef.current.getBoundingClientRect();
      const chartDom = myChart.getDom();
      const chartRect = chartDom.getBoundingClientRect();

      // Skip if EventBox hasn't been rendered yet (width/height are 0)
      if (eventBoxRect.width === 0 || eventBoxRect.height === 0) {
        return false;
      }

      // Convert eventBox position to chart-relative coordinates
      const eventBoxRelative = {
        left: eventBoxRect.left - chartRect.left,
        top: eventBoxRect.top - chartRect.top,
        right: eventBoxRect.right - chartRect.left,
        bottom: eventBoxRect.bottom - chartRect.top,
      };

      // Skip if EventBox is positioned outside the chart area (negative positions or beyond chart bounds)
      if (
        eventBoxRelative.right < 0 || 
        eventBoxRelative.left > chartRect.width ||
        eventBoxRelative.bottom < 0 || 
        eventBoxRelative.top > chartRect.height
      ) {
        return false;
      }

      const option = myChart.getOption();
      const series = option.series || [];

      let intersectionFound = false;
      let intersectionDetails = [];

      // Check intersection with series data points and lines
      for (let seriesIndex = 0; seriesIndex < series.length; seriesIndex++) {
        const seriesItem = series[seriesIndex];
        const seriesModel = myChart.getModel().getSeriesByIndex(seriesIndex);
        
        // Skip hidden series (TopTipsEvent) and series without model
        if (!seriesModel || seriesItem.name === 'TopTipsEvent') {
          continue;
        }

        const data = seriesModel.getData();
        const count = data.count();

        // Log first and last point positions for this series to understand the range
        // For line series, we need to convert from data coordinates to pixel coordinates
        if (count > 0) {
          try {
            // Get the x and y values for first and last points
            const firstValue = [data.get('x', 0) || data.get(seriesItem.encode?.x || 0, 0), data.get('y', 0) || data.get(seriesItem.encode?.y || 1, 0)];
            const lastValue = [data.get('x', count - 1) || data.get(seriesItem.encode?.x || 0, count - 1), data.get('y', count - 1) || data.get(seriesItem.encode?.y || 1, count - 1)];
            
            // Convert to pixel coordinates
            const firstPoint = myChart.convertToPixel({ seriesIndex }, firstValue);
            const lastPoint = myChart.convertToPixel({ seriesIndex }, lastValue);
            
          } catch (error) {
            console.log(`[Intersection] Could not get range for series ${seriesIndex}:`, error);
          }
        }

        // Check each data point in the series
        for (let dataIndex = 0; dataIndex < count; dataIndex++) {
          try {
            // For line series, convert data values to pixel coordinates
            const xValue = data.get('x', dataIndex) !== undefined ? data.get('x', dataIndex) : dataIndex;
            const yValue = data.get('y', dataIndex) !== undefined ? data.get('y', dataIndex) : data.get(seriesItem.encode?.y || 1, dataIndex);
            
            // Convert to pixel position
            const pixelPoint = myChart.convertToPixel({ seriesIndex }, [xValue, yValue]);
            
            if (pixelPoint && Array.isArray(pixelPoint)) {
              const [x, y] = pixelPoint;
              
              // Check if point is within eventBox bounds (with some padding for labels)
              const labelPadding = 5; // Reduced from 20 to be more precise
              const isIntersecting = 
                x >= eventBoxRelative.left - labelPadding &&
                x <= eventBoxRelative.right + labelPadding &&
                y >= eventBoxRelative.top - labelPadding &&
                y <= eventBoxRelative.bottom + labelPadding;

              if (isIntersecting) {
                const detail = {
                  type: 'point',
                  series: seriesItem.name,
                  seriesIndex,
                  dataIndex,
                  point: [x, y],
                  distance: {
                    left: x - eventBoxRelative.left,
                    right: eventBoxRelative.right - x,
                    top: y - eventBoxRelative.top,
                    bottom: eventBoxRelative.bottom - y
                  }
                };
                intersectionDetails.push(detail);
                intersectionFound = true;
              }
            }
          } catch (error) {
            // Skip points that can't be converted
            continue;
          }
        }

        // Check line segments for line/area series
        if (seriesItem.type === 'line' || seriesItem.type === 'area') {
          for (let i = 0; i < count - 1; i++) {
            try {
              // Get data values for both points
              const xValue1 = data.get('x', i) !== undefined ? data.get('x', i) : i;
              const yValue1 = data.get('y', i) !== undefined ? data.get('y', i) : data.get(seriesItem.encode?.y || 1, i);
              const xValue2 = data.get('x', i + 1) !== undefined ? data.get('x', i + 1) : i + 1;
              const yValue2 = data.get('y', i + 1) !== undefined ? data.get('y', i + 1) : data.get(seriesItem.encode?.y || 1, i + 1);
              
              // Convert to pixel positions
              const point1 = myChart.convertToPixel({ seriesIndex }, [xValue1, yValue1]);
              const point2 = myChart.convertToPixel({ seriesIndex }, [xValue2, yValue2]);

              if (point1 && point2 && Array.isArray(point1) && Array.isArray(point2)) {
                const [x1, y1] = point1;
                const [x2, y2] = point2;

                // Check if line segment intersects with eventBox
                if (lineIntersectsRect(x1, y1, x2, y2, eventBoxRelative)) {
                  const detail = {
                    type: 'line',
                    series: seriesItem.name,
                    seriesIndex,
                    segment: [i, i + 1],
                    line: [[x1, y1], [x2, y2]]
                  };
                  intersectionDetails.push(detail);
                  intersectionFound = true;
                }
              }
            } catch (error) {
              // Skip segments that can't be converted
              continue;
            }
          }
        }

        // Check bar rectangles for bar series
        if (seriesItem.type === 'bar') {
          for (let dataIndex = 0; dataIndex < count; dataIndex++) {
            try {
              // For bar series, getItemLayout returns the bar's bounding box
              const barLayout = data.getItemLayout(dataIndex);
              
              if (barLayout) {
                // barLayout contains: { x, y, width, height }
                const barRect = {
                  left: barLayout.x,
                  top: barLayout.y,
                  right: barLayout.x + barLayout.width,
                  bottom: barLayout.y + barLayout.height
                };

                // Check if bar rectangle overlaps with eventBox
                const isOverlapping = !(
                  barRect.right < eventBoxRelative.left ||
                  barRect.left > eventBoxRelative.right ||
                  barRect.bottom < eventBoxRelative.top ||
                  barRect.top > eventBoxRelative.bottom
                );

                if (isOverlapping) {
                  const detail = {
                    type: 'bar',
                    series: seriesItem.name,
                    seriesIndex,
                    dataIndex,
                    barRect: barRect,
                    overlap: {
                      horizontal: Math.min(barRect.right, eventBoxRelative.right) - Math.max(barRect.left, eventBoxRelative.left),
                      vertical: Math.min(barRect.bottom, eventBoxRelative.bottom) - Math.max(barRect.top, eventBoxRelative.top)
                    }
                  };
                  intersectionDetails.push(detail);
                  intersectionFound = true;
                }
              }
            } catch (error) {
              // Skip bars that can't be processed
              continue;
            }
          }
        }
      }

      // Debugging purpose
      // if (intersectionFound) {
      //   console.log('[Intersection] 🎯 TOTAL INTERSECTIONS FOUND:', intersectionDetails.length);
      //   console.log('[Intersection] Details:', intersectionDetails);
      // } else {
      //   console.log('[Intersection] ❌ No intersections found');
      // }

      return intersectionFound;
    } catch (error) {
      console.error('[Intersection] Error checking intersection:', error);
      return false;
    }
  };

  /**
   * Check if a line segment intersects with a rectangle
   */
  const lineIntersectsRect = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    rect: { left: number; top: number; right: number; bottom: number }
  ) => {
    // Check if either endpoint is inside the rectangle
    if (
      (x1 >= rect.left && x1 <= rect.right && y1 >= rect.top && y1 <= rect.bottom) ||
      (x2 >= rect.left && x2 <= rect.right && y2 >= rect.top && y2 <= rect.bottom)
    ) {
      return true;
    }

    // Check intersection with rectangle edges
    return (
      lineIntersectsLine(x1, y1, x2, y2, rect.left, rect.top, rect.right, rect.top) ||
      lineIntersectsLine(x1, y1, x2, y2, rect.right, rect.top, rect.right, rect.bottom) ||
      lineIntersectsLine(x1, y1, x2, y2, rect.right, rect.bottom, rect.left, rect.bottom) ||
      lineIntersectsLine(x1, y1, x2, y2, rect.left, rect.bottom, rect.left, rect.top)
    );
  };

  /**
   * Check if two line segments intersect
   */
  const lineIntersectsLine = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    x3: number,
    y3: number,
    x4: number,
    y4: number
  ) => {
    const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
    if (denominator === 0) return false;

    const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
    const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

    return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
  };

  /**
   * Highlights the current event's X-axis label on hover.
   * Instead of triggering a tooltip, it directly modifies the chart's xAxis data structure 
   * to apply a darker color and bold weight to the text at the specific date index.
   */
  const handleBoxMouseEnter = () => {
    setEventLineShow(true);
    
    // Check for intersection with series/labels
    const hasIntersect = checkIntersection();
    setHasIntersection(hasIntersect);

    if (myChart) {
      // Use current chart xAxis, not prop xAxisData, to avoid index drift when
      // chart option injects extra categories (such as a year separator marker).
      const currentXAxisData = getCurrentXAxisData();
      // 1. Get the formatted date string that matches the X-axis data format
      const realXAxisValue = getRealXAxisValue({
        xAxis: xAxisValue,
        dateType,
        format,
        xAxisData: currentXAxisData,
      });

      // 2. Find the index of this date in the chart's current X-axis data
      const dataIndex = currentXAxisData.indexOf(realXAxisValue);

      if (dataIndex !== -1) {
        // 3. Create a new data array for the X-axis.
        // ECharts allows passing an object for specific items to customize their style.
        const newData = currentXAxisData.map((item, index) => {
          if (index === dataIndex) {
            return {
              value: item,
              textStyle: {
                color: '#262832', // Highlight color (darker)
                fontWeight: 'bold' // Bold the text
              }
            };
          }
          return item;
        });

        // 4. Update the chart configuration to apply the highlight
        myChart.setOption({
          xAxis: {
            data: newData
          }
        });
      }
    }
  };

  // 根据当前事件在 xAxisData 中的位置计算 bubble 的 placement
  const getBubblePlacement = (): 'top' | 'left' | 'right' => {
    // 使用 getRealXAxisValue 转换 xAxisValue 格式，使其与 xAxisData 中的格式一致
    const realXAxisValue = getRealXAxisValue({
      xAxis: xAxisValue,
      dateType,
      format,
      xAxisData,
    });

    const currentIndex = xAxisData?.findIndex((item) => item === realXAxisValue);
    if (currentIndex === -1) return 'top';

    const totalLength = xAxisData?.length;
    const position = currentIndex / totalLength;

    // 前 10% 显示在右侧
    if (position <= 0.1) {
      return 'right';
    }
    // 后 10% 显示在左侧
    if (position >= 0.9) {
      return 'left';
    }
    // 其他情况显示在顶部
    return 'top';
  };

  console.log('eventList', eventList);

  // Flatten eventList so each individual event becomes its own row
  // Each flattened item carries the parent's icon, game_title, and other info
  const flattenedEvents = eventList.reduce((acc: any[], item: any) => {
    const contents = item.event_contents || [item.event_content];
    contents.forEach((content: string) => {
      acc.push({
        ...item,
        _flatEventContent: content,
      });
    });
    return acc;
  }, []);

  const totalEventCount = flattenedEvents.length;

  // 1 event  → show 1 row
  // 2 events → show 2 rows
  // 3+ events → show 1 row + "+N Events"
  const displayItems = totalEventCount <= 2 ? flattenedEvents : flattenedEvents.slice(0, 1);
  const remainingCount = totalEventCount > 2 ? totalEventCount - 1 : 0;

  // Build bubble content: full detail view for all events (shown on hover)
  const bubbleContent = (
    <div className="dbs-event-bubble-wrapper">
      <div className="dbs-event-bubble-date-header">
        {moment(xAxisValue).format(format)} Events
      </div>
      <div className="dbs-event-bubble-detail">
        {eventList.map((item: any, index: number) => (
          <div key={item.id || index} className="dbs-event-bubble-detail__item">
            <div className="dbs-event-bubble-detail__header">
              <img
                src={item.cover || GameDefault}
                alt={item.game_title || ''}
                className="dbs-event-bubble-detail__icon"
              />
              <div
                className={`dbs-event-bubble-detail__info${
                  item.studio_name ? '' : ' dbs-event-bubble-detail__info--title-only'
                }`}
              >
                <span className="dbs-event-bubble-detail__title">{item.game_title}</span>
                {item.studio_name && (
                  <span className="dbs-event-bubble-detail__studio">{item.studio_name}</span>
                )}
              </div>
            </div>
            <div className="dbs-event-bubble-detail__desc-list">
              {(item.event_contents || [item.event_content]).map((content: string, idx: number) => (
                <div key={idx} className="dbs-event-bubble-detail__desc">
                  <span className="dbs-event-bubble-detail__bullet">·</span>
                  <span className="dbs-event-bubble-detail__desc-text">{content}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* Mark area having styling display: none because its not in used in mgmt currently (keep it for future enhancement) */}
      <div
        className={`dbs-mark-area-box ${!showEventAxis ? '--long' : ''} ${markAreaShow ? '--show' : '--hide'
          }`}
        style={renderMarkArea}
      >
        <div
          className="dbs-mark-area-time"
          style={{ left: halfBarWidth + 'px' }}
        >
          <p className="dbs-mark-time-item">{markAreaTimes}</p>
        </div>
      </div>

      <div
        ref={eventBoxRef}
        className={`dbs-event-item-wrapper ${hide ? '--hide' : ''}`}
        // Intersection logic disabled - uncomment below if needed in future (if inersect with graph, then event item will become 0.5 transparent)
        // className={`dbs-event-item-wrapper ${hide ? '--hide' : ''} ${hasIntersection ? '--intersecting' : ''}`}
      >
        <CommonBubble
          className='dbs-event-item-bubble'
          content={bubbleContent}
          // placement={getBubblePlacement()}
          placement="right-start"
          dark={true}
        >
          <div
            className={`dbs-event-box ${eventBoxClassName}`.trim()}
            onMouseLeave={() => handleMouseLeave()}
            onMouseEnter={() => handleBoxMouseEnter()}
          >
            {displayItems.map((item: any, index: number) => (
              <div
                key={`${item.id || index}-${item._flatEventContent}`}
                className="dbs-event-compact-row"
                onMouseEnter={() => handleMouseEnter(item)}
              >
                <img
                  src={item.cover || GameDefault}
                  alt={item.game_title || ''}
                  className="dbs-event-compact-row__icon"
                />
                <span className="dbs-event-compact-row__text">
                  <span className="dbs-event-compact-row__title">{item.game_title_abbr || item.game_title}:</span>
                  {' '}
                  {item._flatEventContent}
                </span>
              </div>
            ))}
            {remainingCount > 0 && (
              <div className="dbs-event-compact-more">
                +{remainingCount} Events
              </div>
            )}
          </div>
        </CommonBubble>
      </div>
      <div
        ref={eventLineRef}
        className={`dbs-event-box-line ${eventLineShow ? '--highlight' : ''}`}
      ></div>
    </>
  );
}
